import type {
  AdyenCheckoutError,
  CoreConfiguration,
  GooglePayConfiguration,
  ApplePayConfiguration,
  UIElement,
  SubmitActions,
} from '@adyen/adyen-web';
import type { MutableRefObject } from 'react';
import {
  ADYEN_CARD_BRANDS,
  ADYEN_PAYMENT_METHOD_TYPE,
  ADYEN_PAYMENT_METHOD_NAME,
  ADYEN_CARD_FIELD,
  ADYEN_DATE_FIELD,
  ADYEN_CVC_FIELD,
  ADYEN_GOOGLE_PAY_CONFIG,
  ADYEN_GOOGLE_PAY_TYPE,
  ADYEN_APPLE_PAY_TYPE,
  ADYEN_SECURED_FIELD_IFRAMES_LOAD_ERROR,
  AdyenCheckoutErrorTypes,
} from '@/constants/adyen';
import type {
  Customer,
  SavedCard,
  PaymentMethod as CheckoutPaymentMethod,
  AdditionalInputField,
  Pricing,
  PriceBreakdown,
} from '@/types/checkout';
import type { AddressDetails } from '@/types/context';
import { ProviderEnum } from '@/types/common';
import { parseAddress, parseAddressObject, type ParsedAddress } from './address';
import type { Address } from '@/api/address';
import { AppchargeErrorType, PaymentStatus, type ResultCode } from '@/types/payment';
import { getPaymentRequestAmount } from './pricing';
import {
  type AdyenConfig,
  type AdyenGooglePayConfig,
  type AdyenApplePayConfig,
  type AdyenApplePayPaymentMethod,
  type CardDetails,
  type CardFieldEvent,
  type AdyenCardComponent,
  type CreateGooglePayComponentParams,
  type CreateApplePayComponentParams,
  type ChallengeWindowSize,
  AdyenInputType,
} from '@/types/adyen';
import { isLandscape, isMobileDevice } from './device';
import { getBrowserInfo } from './browser';
import { shouldUseWalletDerivedZipTax } from './tax';
import { FeatureFlag } from '@/constants/featureFlags';
import { normalizeToAdyenAmount } from './normalizeToAdyenAmount';

export const mountAdyenComponent = (element: UIElement, container: HTMLElement | null) => {
  if (container) {
    element.mount(container);
  }
};

export const createAdyenConfiguration = (config: AdyenConfig, customer?: Customer): CoreConfiguration => {
  return {
    paymentMethodsResponse: {
      paymentMethods: [
        {
          brands: [...ADYEN_CARD_BRANDS],
          name: ADYEN_PAYMENT_METHOD_NAME,
          type: ADYEN_PAYMENT_METHOD_TYPE,
        },
      ],
    },
    environment: config.env || 'test',
    countryCode: customer?.geoLocator?.countryCode2 || customer?.taxCountry,
    clientKey: config.clientKey,
    showPayButton: false,
  };
};

export const isValidCardDetails = (details: CardDetails): boolean =>
  !!(details.bin && details.endDigits && details.expiryDate);

export const createCardDetails = (): CardDetails => ({
  bin: '',
  endDigits: '',
  expiryDate: '',
  cardType: '',
});

export const getAutoAdvanceAdyenFieldFocus = (
  event: Pick<CardFieldEvent, 'valid' | 'fieldType'>,
  cardComponent?: Pick<AdyenCardComponent, 'setFocusOn'>
) => {
  if (!event.valid || !cardComponent?.setFocusOn) return;

  const nextField =
    event.fieldType === ADYEN_CARD_FIELD
      ? ADYEN_DATE_FIELD
      : event.fieldType === ADYEN_DATE_FIELD
        ? ADYEN_CVC_FIELD
        : null;

  if (!nextField) return;

  setTimeout(() => cardComponent.setFocusOn?.(nextField), 0);
};

export const getAdyenConfig = (paymentMethods: unknown[], selectedMethod: string): AdyenConfig | undefined => {
  const method = (paymentMethods as { paymentMethodName: string; publicConfig?: AdyenConfig }[]).find(
    (pm) => pm.paymentMethodName === selectedMethod
  );
  return method?.publicConfig;
};

export interface AdyenCardPaymentParams {
  email: string;
  cardholderName: string;
  shouldSaveCard: boolean;
  paymentMethod: Record<string, unknown>;
  browserInfo: Record<string, unknown>;
  cardDetails: CardDetails;
  paymentMethodId: string;
  paymentMethodDisplayName: string;
  forterToken?: string;
}

export const buildAdyenSavedCreditCardPaymentRequest = (
  selectedCard: SavedCard,
  customer: Customer,
  adyenSettings: CheckoutPaymentMethod,
  cvc: string
) => {
  return {
    paymentProvider: ProviderEnum.ADYEN,
    receipt_email: customer.email,
    paymentMethodDisplayName: adyenSettings.paymentMethodName,
    paymentMethodId: adyenSettings.id,
    params: {
      paymentToken: selectedCard.pspCardIdentifier,
      paymentMethodToken: selectedCard.pspCardIdentifier,
      cvc,
      brand: selectedCard.brand,
      browserInfo: getBrowserInfo(),
    },
  };
};

export const getAdyenCardData = (
  cardDetails: CardDetails,
  paymentMethodData?: Record<string, unknown>
): {
  tokenResponse?: {
    paymentMethodData: Record<string, unknown>;
    browserInfo: Record<string, unknown>;
  };
  cardDetails: {
    bin: string;
    last4digits: string;
    cardType: string;
    ccExpMonth: string;
    ccExpYear: string;
    brand: string;
  };
} | null => {
  if (!isValidCardDetails(cardDetails)) {
    return null;
  }

  const [expMonth, expYear] = cardDetails.expiryDate.split('/');

  return {
    tokenResponse: paymentMethodData
      ? {
          paymentMethodData,
          browserInfo: cardDetails.browserInfo || {},
        }
      : undefined,
    cardDetails: {
      bin: cardDetails.bin,
      last4digits: cardDetails.endDigits,
      cardType: cardDetails.cardType || 'scheme',
      ccExpMonth: expMonth || '',
      ccExpYear: expYear || '',
      brand: cardDetails.cardType || 'scheme',
    },
  };
};

export const createAdyenCardPayment = (params: AdyenCardPaymentParams) => ({
  paymentProvider: 'adyen',
  receipt_email: params.email,
  paymentMethodDisplayName: params.paymentMethodDisplayName,
  paymentMethodId: params.paymentMethodId,
  params: {
    browserInfo: params.browserInfo,
    paymentMethod: {
      ...(params.cardDetails.paymentMethodData || {}),
      holderName: params.cardholderName,
    },
    cardHolderName: params.cardholderName,
    shouldSaveCard: params.shouldSaveCard,
    cardDetails: {
      bin: params.cardDetails.bin,
      lastFourDigits: params.cardDetails.endDigits,
      nameOnCard: params.cardholderName,
      expirationMonth: params.cardDetails.expiryDate.split('/')[0],
      expirationYear: params.cardDetails.expiryDate.split('/')[1],
      cardType: params.cardDetails.cardType || 'scheme',
    },
  },
  ...(params.forterToken && {
    fraudDetection: {
      forterTokenCookie: params.forterToken,
    },
  }),
});

const transformShopperName = (name: string): { firstName: string; lastName: string } => {
  const [firstName, ...rest] = name.trim().split(/\s+/);
  return { firstName: firstName || '', lastName: rest.join(' ') || '' };
};

const buildBillingAddress = (
  billingAddress: string | Address,
  customer: Customer,
  addressDetails?: AddressDetails
): ParsedAddress | undefined => {
  const country = customer.taxCountry ?? customer.geoLocator?.countryCode2;

  if (typeof billingAddress === 'string') {
    return parseAddress(billingAddress, addressDetails?.zipCode, country);
  }

  return parseAddressObject(billingAddress, country);
};

export const buildAdyenGenericPaymentRequest = (
  params: Record<string, string | Address>,
  adyenSettings: CheckoutPaymentMethod,
  customer: Customer,
  addressDetails?: AddressDetails
) => {
  const additionalInputFields: Record<string, unknown> = clonePaymentParams(params);
  if (additionalInputFields.shopperName) {
    additionalInputFields.shopperName = transformShopperName(additionalInputFields.shopperName as string);
  }
  if (additionalInputFields.billingAddress) {
    additionalInputFields.billingAddress = buildBillingAddress(
      additionalInputFields.billingAddress as string | Address,
      customer,
      addressDetails
    );
  }

  const emailFromParams = (params.email as string | undefined) ?? (params.shopperEmail as string | undefined);

  return {
    params: {
      additionalInputFields,
    },
    paymentMethodDisplayName: adyenSettings.paymentMethodDisplayName,
    paymentMethodId: adyenSettings.id,
    paymentProvider: ProviderEnum.ADYEN,
    receipt_email: emailFromParams ?? customer.email ?? '',
  };
};

/**
 * Deep-copy the generic-APM params before we rewrite fields on them.
 *
 * `structuredClone` only exists from Safari 15.4, so on iOS 15.0–15.3 calling
 * it throws a TypeError and takes down every generic Adyen APM (BLIK, PIX, …)
 * at the point of payment. The params are always JSON-safe — they are on their
 * way to the checkout API — so a serialise round-trip is an exact substitute.
 */
const clonePaymentParams = (params: Record<string, string | Address>): Record<string, unknown> =>
  typeof structuredClone === 'function'
    ? structuredClone(params)
    : (JSON.parse(JSON.stringify(params)) as Record<string, unknown>);

export const createAdyenGooglePayConfiguration = (
  config: AdyenGooglePayConfig,
  customer?: Customer,
  paymentMethodConfig?: Record<string, unknown>
): CoreConfiguration => {
  const paymentMethods = [
    paymentMethodConfig && typeof paymentMethodConfig === 'object' && 'type' in paymentMethodConfig
      ? (paymentMethodConfig as { type: string; name: string })
      : { type: ADYEN_GOOGLE_PAY_TYPE, name: 'Google Pay' },
  ];

  const environment = (config.environment || config.env || 'test') as 'test' | 'live';

  return {
    paymentMethodsResponse: {
      paymentMethods,
    },
    environment,
    countryCode: customer?.geoLocator?.countryCode2 || customer?.taxCountry,
    clientKey: config.clientKey,
  };
};

export const buildAdyenGooglePayPaymentRequest = (
  params: {
    paymentMethod: Record<string, unknown>;
    browserInfo?: Record<string, unknown>;
    email?: string;
    zipCode?: string;
  },
  adyenSettings: CheckoutPaymentMethod,
  customer: Customer
) => ({
  paymentProvider: ProviderEnum.ADYEN,
  receipt_email: params.email ?? customer.email ?? '',
  paymentMethodDisplayName: adyenSettings.paymentMethodDisplayName,
  paymentMethodId: adyenSettings.id,
  // ACDEV-25067: only sent when the wallet ZIP resolved to a valid taxed total
  // in-sheet, so the BE can recompute the same tax for the charge.
  ...(params.zipCode ? { zipCode: params.zipCode } : {}),
  params: {
    browserInfo: params.browserInfo ?? {},
    paymentMethod: params.paymentMethod,
  },
});

// ACDEV-25067: Google only renders the itemized cart (Subtotal / Tax lines) when
// `transactionInfo` carries non-empty `displayItems` together with
// `totalPriceLabel` and an explicit `checkoutOption`. We build the same shape for
// the initial sheet and for the in-sheet update so the breakdown shows from open
// and stays consistent after the wallet ZIP recalculates tax.
export const buildGooglePayTaxTransactionInfo = (
  priceBreakdown: PriceBreakdown,
  pricing: Pricing,
  countryCode: string
): google.payments.api.TransactionInfo => {
  const subtotal = priceBreakdown.priceTotal - priceBreakdown.tax;
  return {
    countryCode,
    currencyCode: pricing.currencyCode,
    totalPriceStatus: 'FINAL',
    totalPrice: getPaymentRequestAmount(priceBreakdown.priceTotal, pricing),
    totalPriceLabel: 'Total',
    checkoutOption: 'COMPLETE_IMMEDIATE_PURCHASE',
    displayItems: [
      { label: 'Subtotal', type: 'SUBTOTAL', price: getPaymentRequestAmount(subtotal, pricing) },
      { label: 'Tax', type: 'TAX', price: getPaymentRequestAmount(priceBreakdown.tax, pricing) },
    ],
  };
};

export const getGooglePayComponentOptions = (params: CreateGooglePayComponentParams): GooglePayConfiguration => {
  const { googlePayPaymentMethod, customer, pricing, handlers, featureFlags, sessionStartedWithoutValidTax } = params;
  // `onPaymentDataChanged` is not a top-level handler — it must be nested under
  // `paymentDataCallbacks`, so pull it out before spreading the rest.
  const { onPaymentDataChanged, ...restHandlers } = handlers;

  const gatewayMerchantId = String(
    (googlePayPaymentMethod?.publicConfig as AdyenGooglePayConfig | undefined)?.merchantId ?? ''
  );
  const googlePayConfiguration = googlePayPaymentMethod?.paymentMethodConfig?.configuration ?? {};

  // ACDEV-25067 + ACDEV-27914: US/CA + (`WALLET_ZIP_TAX` ON OR session
  // started without a resolved tax) need the buyer's ZIP for tax. Google
  // only exposes it pre-authorization through the shipping address, and
  // live in-sheet repricing (Dynamic Price Updates) additionally requires a
  // shipping option plus the SHIPPING_ADDRESS / SHIPPING_OPTION callback
  // intents. Gate all of it on the composite so non-US/CA sheets AND
  // FF-OFF sessions with a resolved boot ZIP stay shipping-free.
  const collectZipForTax = customer
    ? shouldUseWalletDerivedZipTax(customer, featureFlags, sessionStartedWithoutValidTax)
    : false;
  const taxShippingConfig: Partial<GooglePayConfiguration> =
    collectZipForTax && onPaymentDataChanged
      ? {
          callbackIntents: ['SHIPPING_ADDRESS'],
          shippingAddressRequired: true,
          paymentDataCallbacks: { onPaymentDataChanged },
        }
      : {};

  const countryCode = customer?.geoLocator?.countryCode2 ?? customer?.taxCountry ?? '';

  return {
    ...ADYEN_GOOGLE_PAY_CONFIG,
    ...taxShippingConfig,
    configuration: {
      gatewayMerchantId,
      ...googlePayConfiguration,
    },
    transactionInfo: collectZipForTax
      ? buildGooglePayTaxTransactionInfo(pricing.priceBreakdown, pricing, countryCode)
      : {
          displayItems: [],
          countryCode,
          currencyCode: pricing.currencyCode,
          totalPriceStatus: 'FINAL',
          totalPrice: getPaymentRequestAmount(pricing.priceBreakdown.priceTotal, pricing),
          totalPriceLabel: 'Total',
        },
    ...(restHandlers as GooglePayConfiguration),
  };
};

// Apple Pay merchant validation requires `domainName` (Apple's
// `initiativeContext`) to equal the TOP-LEVEL document's domain — the address
// bar the player sees — not the checkout iframe's own origin. (ACDEV-25945)
//
// Safari Private Browsing strips `document.referrer` to '' inside cross-origin
// iframes, so the previous `document.referrer || document.location.href`
// fallback resolved to the iframe's own host (pay.appcharge.com). Adyen then
// merchant-validated the wrong domain and PassKit rejected the otherwise-valid
// session ("Application failed to provide a valid merchant session"), breaking
// Apple Pay in Private Browsing while normal browsing kept working.
//
// `location.ancestorOrigins` is NOT stripped in Private Browsing and gives the
// real top-level origin, so prefer it and only fall back to referrer.
export const getTopLevelDomain = (): string => {
  try {
    if (window.self === window.top) {
      return window.location.hostname;
    }
    const ancestorOrigins = window.location.ancestorOrigins;
    if (ancestorOrigins?.length) {
      return new URL(ancestorOrigins[ancestorOrigins.length - 1]).hostname;
    }
    if (document.referrer) {
      return new URL(document.referrer).hostname;
    }
    return window.location.hostname;
  } catch {
    return window.location.hostname;
  }
};

export const getAdyenApplePayConfig = (): ApplePayConfiguration => {
  return {
    isExpress: true,
    showPayButton: true,
    // Apple returns an email ONLY on the shipping contact: it honors
    // `postalAddress` in `requiredBillingContactFields` and silently ignores
    // `email` there (adyen-web#3393). Requesting email on the billing side
    // alone left `receipt_email` empty and failed finalize for every session
    // without a checkout email. An email-only shipping request renders a single
    // "Contact" row, no address section — the billing contact still carries the
    // postal address for tax (see `useAdyenApplePayBillingZipTax`).
    requiredBillingContactFields: ['email'],
    requiredShippingContactFields: ['email'],
    domainName: getTopLevelDomain(),
  };
};

export const createAdyenApplePayConfiguration = (
  applePayPaymentMethod: AdyenApplePayPaymentMethod,
  customer?: Customer
): CoreConfiguration => {
  const config = applePayPaymentMethod.publicConfig as AdyenApplePayConfig;
  const paymentMethodConfig = applePayPaymentMethod.paymentMethodConfig;
  const paymentMethods = [
    paymentMethodConfig && typeof paymentMethodConfig === 'object' && 'type' in paymentMethodConfig
      ? (paymentMethodConfig as { type: string; name: string })
      : { type: ADYEN_APPLE_PAY_TYPE, name: 'Apple Pay' },
  ];

  const environment = (config.environment || config.env || 'test') as 'test' | 'live';

  return {
    paymentMethodsResponse: {
      paymentMethods,
    },
    environment,
    countryCode: customer?.geoLocator?.countryCode2 || customer?.taxCountry,
    clientKey: config.clientKey,
  };
};

export const buildAdyenApplePayPaymentRequest = (
  params: {
    paymentMethod: Record<string, unknown>;
    paymentMethodDetails: Record<string, unknown>;
    email?: string;
    zipCode?: string;
  },
  adyenSettings: CheckoutPaymentMethod,
  customer: Customer
) => ({
  paymentProvider: ProviderEnum.ADYEN,
  receipt_email: params.email ?? customer.email ?? '',
  paymentMethodDisplayName: adyenSettings.paymentMethodDisplayName,
  paymentMethodId: adyenSettings.id,
  // ACDEV-25067: only sent when the wallet ZIP resolved to a valid taxed total
  // in-sheet, so the BE can recompute the same tax for the charge.
  ...(params.zipCode ? { zipCode: params.zipCode } : {}),
  params: {
    paymentMethod: {
      ...params.paymentMethod,
      type: 'applepay',
    },
    paymentMethodDetails: params.paymentMethodDetails,
  },
});

// ACDEV-25067: Apple Pay shows line items natively (no shipping-method row
// needed, unlike Google Pay). We build the Subtotal / Tax breakdown + the new
// total once and reuse it for the initial sheet and for the `onPaymentMethodSelected`
// update so the displayed total and breakdown stay consistent after the wallet
// ZIP recalculates tax. Return shape is structurally identical to
// `ApplePayPaymentMethodUpdate` (both share `newTotal` + `newLineItems`).
export const buildApplePayTaxUpdate = (
  priceBreakdown: PriceBreakdown,
  pricing: Pricing,
  totalLabel = 'Total'
): { newTotal: ApplePayJS.ApplePayLineItem; newLineItems: ApplePayJS.ApplePayLineItem[] } => {
  const subtotal = priceBreakdown.priceTotal - priceBreakdown.tax;
  return {
    newTotal: { label: totalLabel, amount: getPaymentRequestAmount(priceBreakdown.priceTotal, pricing), type: 'final' },
    newLineItems: [
      { label: 'Subtotal', amount: getPaymentRequestAmount(subtotal, pricing), type: 'final' },
      { label: 'Tax', amount: getPaymentRequestAmount(priceBreakdown.tax, pricing), type: 'final' },
    ],
  };
};

export const getApplePayComponentOptions = (params: CreateApplePayComponentParams): ApplePayConfiguration => {
  const {
    applePayPaymentMethod,
    customer,
    pricing,
    handlers,
    featureFlags,
    sessionStartedWithoutValidTax,
    companyName,
  } = params;
  const { onPaymentMethodSelected, ...restHandlers } = handlers;

  const applePayConfiguration = applePayPaymentMethod?.paymentMethodConfig?.configuration ?? {};

  // ACDEV-26742: brand the sheet's grand-total row with the company name
  // ("PAY ACME GAMES"), per Apple's payment-sheet guidance.
  // `totalPriceLabel` maps to PassKit's `total.label` and is DISPLAY-ONLY —
  // it is never sent to Adyen's /payments call nor to the bank. The bank
  // statement stays owned by the BE `shopperStatement` + the Adyen
  // dynamic-value transaction-description config. When the flag is OFF or no
  // companyName is available we set nothing, preserving the SDK's own
  // fallback to `configuration.merchantName`.
  const totalLabel = getApplePayTotalLabel(companyName, featureFlags);

  // ACDEV-25067 + ACDEV-27914: US/CA + (`WALLET_ZIP_TAX` ON OR session
  // started without a resolved tax): request the BILLING postal address so
  // Apple surfaces the card's billing ZIP pre-authorization (via
  // `onPaymentMethodSelected`) and render the Subtotal / Tax breakdown.
  // Non-tax countries and FF-OFF sessions that already have a resolved boot
  // tax keep the base sheet (email-only Contact row). Adding `postalAddress`
  // here does NOT move email collection: that stays on the shipping contact
  // from `getAdyenApplePayConfig`.
  const collectZipForTax = customer
    ? shouldUseWalletDerivedZipTax(customer, featureFlags, sessionStartedWithoutValidTax)
    : false;
  const taxBillingConfig: Partial<ApplePayConfiguration> =
    collectZipForTax && onPaymentMethodSelected
      ? {
          requiredBillingContactFields: ['email', 'postalAddress'],
          totalPriceLabel: totalLabel ?? 'Total',
          lineItems: buildApplePayTaxUpdate(pricing.priceBreakdown, pricing).newLineItems,
          onPaymentMethodSelected,
        }
      : {};

  return {
    ...getAdyenApplePayConfig(),
    ...(totalLabel ? { totalPriceLabel: totalLabel } : {}),
    ...taxBillingConfig,
    amount: {
      value: normalizeToAdyenAmount(pricing.priceBreakdown.priceTotal, pricing.currencyCode),
      currency: pricing.currencyCode,
    },
    configuration: applePayConfiguration,
    ...restHandlers,
  };
};

// ACDEV-26742: Apple Pay grand-total row label = the trimmed company name,
// per Apple's payment-sheet guidance (the sheet renders the "PAY" prefix
// itself, so the row reads "PAY ACME GAMES  $10.99" — the word "Total" is
// deliberately designed out of the sheet by the platform).
// Returns `undefined` when the `APPLE_PAY_COMPANY_NAME_LABEL` flag is OFF or
// the name is missing/blank, so every caller falls back to its existing
// default (`configuration.merchantName` via the Adyen SDK, or the literal
// 'Total' in the ZIP-tax breakdown path). The flag is the rollout/rollback
// switch: OFF restores pre-flag behavior byte-for-byte.
export const getApplePayTotalLabel = (
  companyName: string | undefined,
  featureFlags: Record<FeatureFlag, boolean>
): string | undefined => {
  if (featureFlags[FeatureFlag.APPLE_PAY_COMPANY_NAME_LABEL] !== true) return undefined;
  return companyName?.trim() || undefined;
};

export const hasAPMField = (additionalInputFields: AdditionalInputField[], field: string): boolean => {
  return additionalInputFields.some((f) => f.apm_key === field);
};

// The BE over-escapes regex shorthand classes: it sends `\\s` (an escaped
// backslash followed by `s`) where `\s` (whitespace) was meant. Fed verbatim to
// `new RegExp(...)` this matches a literal backslash, so e.g. the `Full Name`
// pattern `^[a-zA-Z\\s]+$` rejects any value containing a space ("John Doe")
// while wrongly accepting space-less input ("JohnDoe"). Collapse a doubled
// backslash that precedes an ASCII letter (\s \d \w \b ...) back to a single
// backslash. Already-correct single-backslash patterns are left untouched.
const normalizeOverEscapedRegex = (pattern: string): string => pattern.replace(/\\\\(?=[a-zA-Z])/g, '\\');

// The BE is inconsistent about how it sends an `additionalInputFields` validation:
// some fields carry a raw pattern (e.g. `^\d+$`) while others wrap it as a JS
// regex literal (e.g. `/^[A-Za-z\s]+$/i`). Passing the wrapped form straight to
// `new RegExp(...)` turns the slashes into literal characters, so the pattern
// matches nothing and every value is rejected. Normalize both shapes here.
export const buildFieldValidationRegex = (validation: string): RegExp => {
  const literalMatch = validation.match(/^\/(.+)\/([a-z]*)$/iu);
  if (literalMatch) {
    const [, pattern, flags] = literalMatch;
    return new RegExp(normalizeOverEscapedRegex(pattern), flags);
  }
  return new RegExp(normalizeOverEscapedRegex(validation), 'u');
};

export const getDefaultFieldValues = (
  additionalInputFields: AdditionalInputField[],
  customer: Customer
): Record<string, string | Address> => {
  const defaults: Record<string, string | Address> = {};

  if (hasAPMField(additionalInputFields, AdyenInputType.SHOPPER_NAME) && customer.name?.trim()) {
    defaults[AdyenInputType.SHOPPER_NAME] = customer.name.trim();
  }

  return defaults;
};

export function mapAdyenResultCodeToPaymentStatus(resultCode: ResultCode | undefined): PaymentStatus {
  if (resultCode === 'Authorised') {
    return PaymentStatus.SUCCEEDED;
  }
  if (resultCode === 'Cancelled') {
    return PaymentStatus.CANCELLED;
  }
  return PaymentStatus.FAILED;
}

// ACDEV-24170: Adyen Apple/Google Pay SDK error codes → appcharge reasons.
// `null` means "silent dismiss" (no UI / no event), used for CANCEL where
// the player just closed the wallet sheet themselves. Any unknown / future
// SDK code falls through to `GENERIC` via `mapAdyenSdkError` so the player
// never sees a raw code or an empty error state.
export const ADYEN_SDK_ERROR_TO_APPCHARGE: Record<AdyenCheckoutErrorTypes, AppchargeErrorType | null> = {
  [AdyenCheckoutErrorTypes.NETWORK_ERROR]: AppchargeErrorType.CONNECTION,
  [AdyenCheckoutErrorTypes.CANCEL]: null,
  [AdyenCheckoutErrorTypes.IMPLEMENTATION_ERROR]: AppchargeErrorType.GENERIC,
  [AdyenCheckoutErrorTypes.API_ERROR]: AppchargeErrorType.GENERIC,
  [AdyenCheckoutErrorTypes.ERROR]: AppchargeErrorType.GENERIC,
  [AdyenCheckoutErrorTypes.SCRIPT_ERROR]: AppchargeErrorType.GENERIC,
  [AdyenCheckoutErrorTypes.SDK_ERROR]: AppchargeErrorType.GENERIC,
};

/**
 * ACDEV-26410: pick the 3DS2 challenge window size for the current viewport.
 *
 * Adyen's default is `'02'` — a *fixed* 390x400px iframe. The challenge modal
 * is `max-w-md` (448px) with `px-5`, so the usable width is
 * `min(viewport, 448) - 40`: 408px on desktop (the iframe fits) but 320px on a
 * 360px Android and 350px on a 390px iPhone, where the iframe overflows and is
 * only reachable by scrolling sideways inside the modal. Production RUM shows
 * desktop completing 3DS challenges at 100% against ~50% on mobile.
 *
 * `'05'` is Adyen's responsive size (100% x 100%), so the challenge fills the
 * container it is mounted into instead of forcing a horizontal scroll.
 *
 * `isLandscape()` is checked too: a phone held sideways reports
 * `innerWidth >= 700` and would fall into the desktop branch, but its ~360-430px
 * *height* cannot fit the fixed 400px iframe — the responsive size is the only
 * one that can adapt to that band.
 */
export const resolve3DSChallengeWindowSize = (): ChallengeWindowSize =>
  isMobileDevice() || isLandscape() ? '05' : '02';

/**
 * Take the card flow's `SubmitActions` off the ref, clearing it.
 *
 * The actions come from a single `onSubmit` and back one promise, so the SDK
 * ignores every settle after the first. The ref was never cleared, which left
 * a spent handle sitting there for the rest of the session: a later payment
 * would find it, settle it to no effect, and silently drop its own result —
 * no failure reported, and the payment-in-progress blocker never lifted.
 * Reading them through here makes "someone still owns this promise" and "the
 * ref is non-empty" the same question again.
 */
export const consumeCardActions = (
  cardDetailsRef: MutableRefObject<CardDetails & { actions?: SubmitActions }>
): SubmitActions | undefined => {
  const actions = cardDetailsRef.current?.actions;
  if (actions) {
    cardDetailsRef.current.actions = undefined;
  }
  return actions;
};

/** Adyen Card `onError` when checkoutshopper secured-field iframes never mount. */
export const isAdyenSecuredFieldsIframeLoadError = (err?: AdyenCheckoutError | null): boolean =>
  err?.message === ADYEN_SECURED_FIELD_IFRAMES_LOAD_ERROR;

export const mapAdyenSdkError = (errorName?: string): AppchargeErrorType | null => {
  if (errorName === AdyenCheckoutErrorTypes.CANCEL) {
    return null;
  }
  if (!errorName) {
    return AppchargeErrorType.GENERIC;
  }
  const known = (ADYEN_SDK_ERROR_TO_APPCHARGE as Record<string, AppchargeErrorType | null>)[errorName];
  // `known` is null only for CANCEL (already handled above), so any
  // remaining nullish value means "unknown code" and must surface GENERIC.
  return known ?? AppchargeErrorType.GENERIC;
};
